/**
 * ساخت سیگنال — قرینه‌ی apps/mobile/app/signal/create.tsx.
 *
 * ترتیبِ بخش‌ها، گزینه‌های زمان و متنِ همه‌ی برچسب‌ها عیناً همونِ موبایله (از
 * همون کلیدهای i18n می‌خونه)، تا کسی که اپ رو بلده اینجا گم نشه.
 *
 * دو چیزِ موبایل عمداً اینجا نیست و هر دو در جدولِ parityِ پلن ثبت شده‌اند:
 *  - **دعوت از دفترچه‌ی گوشی**: مرورگر به مخاطبینِ گوشی دسترسی نداره.
 *    جایگزینِ وبیش لینکِ اشتراکِ خودِ سیگناله (دکمه‌ش در صفحه‌ی سیگنال).
 *  - **پیشنهادِ وقتِ مشترک** از availability: برشِ بعدیه.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { inviteRepo } from '@/core/data/inviteRepo';
import { friendRepo, groupRepo } from '@/core/data/socialRepo';
import { contactFriendRepo } from '@/core/data/contactFriendRepo';
import { phoneOnlyContacts } from '@/core/lib/friendList';
import {
  UNACCEPTED_INVITE_CAP, canInvite, inviteCapHint, isInviteCapError,
} from '@/core/lib/inviteCap';
import { eventRepo, tagRepo } from '@/core/data/miscRepo';
import { kindsFor } from '@/core/domain/signalKinds';
import { APPROX_PRESETS } from '@/core/lib/approxTime';
import { signalWhenLabel } from '@/core/lib/dates';
import { dayFromIso, isoDay } from '@/core/lib/discoverWhen';
import {
  approxDays, choiceOnEvent, eventDaySpan, eventStartClock,
} from '@/core/lib/signalEventDate';
import { t, useLocale } from '@/core/lib/i18n';
import { signalUrl } from '@/core/lib/shareLink';
import { useSession } from '@/core/session';
import { reportError } from '@/core/lib/report';
import { handleFilterBlocked } from '@/ui/filterBlocked';
import { track, trackFunnel } from '@/core/lib/analytics';
import { SIGNAL_NOTE_KEYS } from '@/core/lib/signalNote';
import { Card, Checkbox, Help, Note, PickList, Select } from '@/ui/kit';
import { TagPicker } from '@/ui/TagPicker';
import { MultiSelect, type PickItem } from '@/ui/MultiSelect';
import {
  addInvitee, inviteeLabel, removeInvitee, type DraftInvitee,
} from '@/core/lib/emailInvites';
import { takeCreatePrefill } from './createPrefill';
import { tagLabel } from '@/core/lib/tagLabel';
import { tagIdsOf, type SignalDraft, type SignalKind, type TimeMode } from '@/core/domain/types';

/** همون چهار حالتِ موبایل. سه تای اول یعنی «هنوز دقیق نیست». */
type WhenMode = 'approx' | 'coordinate' | 'exact' | 'free';

export function CreateSignalPage() {
  useLocale();
  const { profile } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [kind, setKind] = useState<SignalKind>('definite');
  const [title, setTitle] = useState('');
  /** شرحِ آزاد — ستونِ `signals.note` که تا حالا هیچ فرمی پرش نمی‌کرد. */
  const [note, setNote] = useState('');
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [whenMode, setWhenMode] = useState<WhenMode>('approx');
  const [preset, setPreset] = useState<string>(APPROX_PRESETS[0].token);
  // تاریخ و ساعت جدا: `datetime-local` بین مرورگرها یکدست نیست، و از وقتی
  // ساعت اختیاری شده، خالی گذاشتنش خودش یه معنیِ درسته («فقط روز»).
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [freeWhen, setFreeWhen] = useState('');
  const [place, setPlace] = useState('');
  const [chain, setChain] = useState(0);
  /** ظرفیت/نصاب متن می‌مانند — فیلدِ عددیِ خالی‌شدنی (مایگریشن 0131). */
  const [capacity, setCapacity] = useState('');
  const [quorum, setQuorum] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  /**
   * «گزینه‌های بیشتر» (زنجیره + مهلت) پیش‌فرض بسته‌ست — کم‌استفاده‌ان
   * (WHOSUP-282). با مقدارِ غیرپیش‌فرض خودش باز می‌مونه تا چیزی پنهان نشه.
   */
  const [moreOpen, setMoreOpen] = useState(false);
  /** ساخت از رخداد: عنوان/کِی/کجا خلاصه‌ی متنی‌اند تا «ویرایش» زده شه. */
  const [eventEditOpen, setEventEditOpen] = useState(false);
  const [everyone, setEveryone] = useState(false);
  /** فرمِ دعوتِ بیرون از اپ تا خواسته نشه باز نمی‌شه. */
  const [outsideOpen, setOutsideOpen] = useState(false);
  /** «یک لینک هم بساز» — قرینه‌ی کارتِ موبایل (app/signal/create.tsx). */
  const [wantLink, setWantLink] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  /* دعوت با ایمیل — کسی که اصلاً حساب نداره. مایگریشن 0066. */
  const [byEmail, setByEmail] = useState<DraftInvitee[]>([]);
  const [emailDraft, setEmailDraft] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const addByEmail = () => {
    const r = addInvitee(byEmail, emailDraft, nameDraft);
    if (!r.ok) {
      // «تکراری» و «پر» خطا نیستن، فقط کاری برای انجام دادن نیست — پیامِ
      // قرمز برای «قبلاً اضافه‌ش کردی» بیشتر گیج می‌کنه تا کمک.
      setEmailError(r.reason === 'invalid' ? t('inviteEmailBad') : null);
      if (r.reason !== 'invalid') { setEmailDraft(''); setNameDraft(''); }
      return;
    }
    setByEmail(r.list);
    setEmailDraft('');
    setNameDraft('');
    setEmailError(null);
  };
  // رخدادی که این سیگنال از روش ساخته می‌شه — هیچ‌جای فرم دیده نمی‌شه، فقط
  // با خودِ سیگنال ذخیره می‌شه (`signals.event_id`).
  const [fromEventId, setFromEventId] = useState<string | undefined>(undefined);

  // پیش‌پرِ آمده از صفحه‌ی رخداد/مکان. یک‌بار خونده و پاک می‌شه.
  useEffect(() => {
    const p = takeCreatePrefill();
    if (!p) return;
    if (p.title) setTitle(p.title);
    if (p.kind) setKind(p.kind);
    if (p.categoryId) setTagIds([p.categoryId]);
    if (p.eventId) setFromEventId(p.eventId);
    if (p.placeName) setPlace(p.placeName);
  }, []);

  /**
   * «کِی»ِ رخدادِ مبدأ — برای دو کار (core/lib/signalEventDate.ts):
   * تک‌تاریخ فرم را پیش‌پر می‌کند، چندتاریخ انتخابِ کاربر را می‌سنجد.
   * همان کلیدِ کشِ صفحه‌ی جزئیات، پس معمولاً بدونِ رفت‌وبرگشتِ تازه.
   */
  const fromEvent = useQuery({
    queryKey: ['event', fromEventId],
    queryFn: () => eventRepo.byId(fromEventId!),
    enabled: !!fromEventId,
  });
  const fromOccurrences = useQuery({
    queryKey: ['eventOccurrences', fromEventId],
    queryFn: () => eventRepo.occurrences(fromEventId!),
    enabled: !!fromEventId,
  });

  /**
   * رخدادی که تاریخش یک‌تکه است: فرم با همان تاریخ (و ساعت) باز می‌شود — «کِی»اش
   * را همان لحظه می‌دانیم و دوباره پرسیدنش بی‌دلیل است. فقط تا وقتی کاربر خودش
   * دست به تاریخ نزده، تا انتخابی را پس نگیریم.
   *
   * بازه‌ی چندروزه هم پیش‌پر می‌شود، ولی فقط با **روزِ اولش**: این فرم یک
   * ورودیِ تاریخ دارد، نه دو تا (برخلافِ تقویمِ موبایل). هشدارِ پایین همچنان
   * کلِ بازه را می‌سنجد، پس روزِ وسطِ جشنواره هم هشدار نمی‌گیرد.
   */
  useEffect(() => {
    if (!fromEvent.data || !fromOccurrences.data) return;
    if (date !== '' || whenMode !== 'approx') return;
    const span = eventDaySpan(fromEvent.data, fromOccurrences.data);
    if (!span) return;
    setWhenMode('exact');
    setDate(isoDay(span.start));
    const clock = fromEvent.data.startsAt ? eventStartClock(fromEvent.data.startsAt) : null;
    if (clock) {
      setTime(`${String(clock.h).padStart(2, '0')}:${String(clock.m).padStart(2, '0')}`);
    }
  }, [fromEvent.data, fromOccurrences.data, date, whenMode]);

  /**
   * تگ‌های خودِ رخداد (همه، نه فقط اولی که از پیش‌پر می‌آید) — مثلِ کِی/کجا
   * از رخداد ثابت‌اند و در خلاصه نشان داده می‌شوند (WHOSUP-282).
   */
  useEffect(() => {
    const ids = fromEvent.data ? tagIdsOf(fromEvent.data) : [];
    if (ids.length) setTagIds(ids);
  }, [fromEvent.data]);

  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });
  const friends = useQuery({
    queryKey: ['friends'],
    queryFn: () => friendRepo.list(profile!.id),
    enabled: !!profile,
  });
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupRepo.list(profile!.id),
    enabled: !!profile,
  });
  const contactFriends = useQuery({
    queryKey: ['contactFriends'],
    queryFn: contactFriendRepo.list,
    enabled: !!profile,
  });

  /**
   * هر کسی که توی فهرستِ دوستانه — تاییدشده **یا** درخواستِ باز (WHOSUP-204).
   *
   * تا دیروز فقط تاییدشده‌ها بودند. حالا کسی که هنوز جواب نداده هم
   * انتخاب‌شدنیه: دعوتِ مستقیم ردیفِ `signal_recipients` می‌سازه و دیده
   * شدنش از همون ردیفه، نه از دوستی. جلوی اسپم رو سقفِ سه‌تایی می‌گیره
   * (مایگریشن 0141)، نه پنهان کردنِ آدم‌ها از فهرست.
   *
   * تاییدشده‌ها اول: اون‌ها کارِ روزمره‌ان و نباید لابه‌لای پندینگ‌ها گم بشن.
   */
  const invitees = useMemo(
    () => [...(friends.data ?? [])]
      .sort((a, b) => (
        Number(b.status === 'accepted') - Number(a.status === 'accepted')
        || a.profile.displayName.localeCompare(b.profile.displayName)
      )),
    [friends.data],
  );

  /**
   * «برای هر کدوم چند دعوتِ دیگه مونده» — فقط برای دوستیِ تاییدنشده.
   *
   * سهمیه‌ی دوستِ تاییدشده بی‌سقفه، پس پرسیدنش فقط ورودیِ کوئری رو بزرگ
   * می‌کنه. کلیدِ کش شناسه‌هاست تا با عوض شدنِ فهرست دوباره پرسیده شه.
   */
  const pendingIds = useMemo(
    () => invitees.filter((f) => f.status !== 'accepted').map((f) => f.profile.id),
    [invitees],
  );
  const allowance = useQuery({
    queryKey: ['inviteAllowance', [...pendingIds].sort().join(',')],
    queryFn: () => signalRepo.inviteAllowance(pendingIds),
    enabled: pendingIds.length > 0,
  });
  const remainingOf = (userId: string): number | undefined => allowance.data?.get(userId);

  /**
   * دوستِ فقط-شماره‌تلفن (WHOSUP-143). موبایل این آدم‌ها رو با پیامک دعوت
   * می‌کنه؛ مرورگر پیامک نداره، پس این‌جا فقط دیده می‌شن — خاکستری و
   * بی‌کلیک، با توضیحِ چرا — تا کاربر فکر نکنه دوستش از فهرست گم شده.
   * فیلترش (بی‌حساب + شماره + بدونِ ایمیل) توی core تسته.
   */
  const phoneOnly = useMemo(
    () => phoneOnlyContacts(contactFriends.data ?? [])
      .sort((a, b) => a.name.localeCompare(b.name)),
    [contactFriends.data],
  );

  const CHAIN = [
    { label: t('chainOff'), value: 0 },
    { label: t('chainPlus1'), value: 1 },
    { label: t('chainPlus2'), value: 2 },
    { label: t('chainUnlimited'), value: -1 },
  ];

  const toggle = (id: string) => {
    setEveryone(false);
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleGroup = (ids: string[]) => {
    setEveryone(false);
    setPicked((prev) => {
      const next = new Set(prev);
      const allOn = ids.length > 0 && ids.every((id) => next.has(id));
      ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  /**
   * خوراکِ انتخابگرِ دعوت: اول گروه‌ها، بعد دوستان — چون انتخابِ یه گروه
   * معمولاً همون کاریه که آدم می‌خواد بکنه و بدونش باید پنج نفر رو تک‌تک بزنه.
   *
   * کلیدها پیشوند دارن (`g-` / `u-`) چون یه فهرستِ واحده و شناسه‌ی گروه عدد
   * است و شناسه‌ی آدم UUID — بدونِ پیشوند، `onToggle` نمی‌دونه کدومه.
   */
  const groupMembers = useMemo(
    () => new Map((groups.data ?? []).map((g) => [`g-${g.id}`, g.memberUserIds])),
    [groups.data],
  );
  const peopleItems: PickItem[] = useMemo(() => [
    ...(groups.data ?? []).map((g) => ({
      key: `g-${g.id}`,
      label: `${g.emoji} ${g.name}`,
      hint: t('membersCount', { n: g.memberUserIds.length }),
      searchText: g.name,
    })),
    ...invitees.map((f) => {
      // دوستیِ تاییدشده سهمیه نداره، پس نه خطِ دوم می‌گیره نه خاکستری می‌شه.
      const rem = f.status === 'accepted' ? -1 : remainingOf(f.profile.id);
      const cap = inviteCapHint(rem);
      return {
        key: `u-${f.profile.id}`,
        label: f.profile.displayName,
        hint: cap ? t(cap.key, { n: cap.n }) : undefined,
        searchText: `${f.profile.displayName} ${f.profile.username}`,
        disabled: !canInvite(rem),
      };
    }),
    // تهِ فهرست، نه لابه‌لای بقیه: خاکستری‌ها انتخاب‌شدنی نیستن و نباید
    // وسطِ اسکنِ چشمیِ «کی رو دعوت کنم» سبز شن.
    ...phoneOnly.map((c) => ({
      key: `c-${c.id}`,
      label: c.name,
      hint: t('phoneOnlyCantPick'),
      searchText: `${c.name} ${c.phones.join(' ')}`,
      disabled: true,
    })),
  ], [groups.data, invitees, phoneOnly, allowance.data]);

  /** گروهی که همه‌ی اعضاش انتخاب شدن، خودش هم انتخاب‌شده حساب می‌شه. */
  const pickedGroupKeys = [...groupMembers.entries()]
    .filter(([, ids]) => ids.length > 0 && ids.every((id) => picked.has(id)))
    .map(([key]) => key);
  const pickedKeys = [...pickedGroupKeys, ...[...picked].map((id) => `u-${id}`)];
  /**
   * چیپِ عضوی که با گروهش انتخاب شده تکراریه: خودِ چیپِ گروه گفتش. توی
   * فهرستِ باز ولی تیکش سرِ جاشه (`pickedKeys`)، چون واقعاً انتخاب‌شده‌ست.
   */
  const coveredByGroup = new Set(pickedGroupKeys.flatMap((k) => groupMembers.get(k) ?? []));
  const chipKeys = pickedKeys.filter((k) => !(k.startsWith('u-') && coveredByGroup.has(k.slice(2))));

  /**
   * بی‌ساعت، ظهرِ محلی ذخیره می‌شه — نه نیمه‌شب. دلیلش در
   * core/lib/dateChoice.ts نوشته شده: با نیمه‌شب، یه جابه‌جاییِ کوچکِ منطقه‌ی
   * زمانی روزِ سیگنال رو یکی عقب/جلو می‌بره.
   */
  const exactStart = (): Date | null => {
    if (!date) return null;
    const [y, m, d] = date.split('-').map(Number);
    if (!y || !m || !d) return null;
    if (!time) return new Date(y, m - 1, d, 12, 0, 0, 0);
    const [hh, mm] = time.split(':').map(Number);
    return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
  };

  /** مهلتِ پاسخ از دو فیلدِ جدا؛ بی‌ساعت یعنی پایانِ همان روز نیست — ظهر است
   *  (همان قاعده‌ی dateChoice) پس ساعت را هم بخواه یا ظهر بپذیر. */
  const composeDeadline = (): Date | null => {
    if (!deadlineDate) return null;
    const [y, m, d] = deadlineDate.split('-').map(Number);
    if (!y || !m || !d) return null;
    if (!deadlineTime) return new Date(y, m - 1, d, 12, 0, 0, 0);
    const [hh, mm] = deadlineTime.split(':').map(Number);
    return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0);
  };

  const send = useMutation({
    mutationFn: async (audience: 'picked' | 'friends') => {
      const start = whenMode === 'exact' ? exactStart() : null;
      const draft: SignalDraft = {
        kind,
        title: title.trim(),
        note: note.trim() || undefined,
        tagIds,
        // «تاریخ انتخاب کردم ولی هنوز نزدم» و «متنِ آزاد» هر دو approx می‌شن —
        // همون نگاشتِ موبایل.
        timeMode: (start ? 'exact'
          : whenMode === 'coordinate' ? 'coordinate' : 'approx') as TimeMode,
        startsAt: start ?? undefined,
        dateOnly: start ? !time : undefined,
        approxLabel: whenMode === 'approx' ? preset
          : whenMode === 'free' ? freeWhen.trim() || undefined : undefined,
        placeName: place.trim() || undefined,
        // هزینه دیگه فیلدِ جدا نیست — یکی از پیشنهادهای پشتِ «؟»ِ شرحه.
        capacityMax: kind === 'hosting' && Number(capacity) > 0 ? Number(capacity) : undefined,
        quorumMin: kind !== 'definite' && Number(quorum) > 0 ? Number(quorum) : undefined,
        rsvpDeadline: composeDeadline() ?? undefined,
        chainLimit: chain,
        audience,
        recipientIds: audience === 'picked' ? [...picked] : [],
        // گروهی که همه‌ی اعضاش انتخاب شدن — همون قاعده‌ای که چیپِ گروه رو
        // «انتخاب‌شده» نشون می‌ده — منشأ دعوت ثبت می‌شه (via = 'group').
        groupPicks: audience === 'picked'
          ? [...groupMembers.entries()]
            .filter(([, ids]) => ids.length > 0 && ids.every((uid) => picked.has(uid)))
            .map(([key, ids]) => ({ groupId: Number(key.slice(2)), memberUserIds: ids }))
          : [],
        eventId: fromEventId,
      };
      const created = await signalRepo.createAndSend(draft, profile!.id);

      // ایمیل **بعد از** ساخت می‌ره، چون لینکِ توی ایمیل به همین سیگنال
      // اشاره داره و قبل از ساخته شدنش وجود نداشت — همون ترتیبی که مسیرِ
      // پیامکِ موبایل هم داره.
      // سیگنالِ نگه‌داشته (0174) برای هیچ‌کس نمی‌رود — ایمیل هم نه.
      if (byEmail.length && created.status !== 'held') {
        try {
          await inviteRepo.byEmail(created.id, byEmail);
        } catch (e) {
          // سیگنال ساخته شده و دوستانِ اپی خبردار شدن؛ شکستِ ایمیل نباید
          // کلِ کار رو شکست‌خورده نشون بده. گزارش می‌شه و کاربر می‌ره جلو.
          reportError('web.signal.inviteByEmail', e);
        }
      }
      return created;
    },
    onSuccess: async (created, audience) => {
      // شمارش، نه محتوا: چند نفر و از چه راهی — نه اینکه چه کسی یا چه چیزی.
      track('signal_created', {
        kind, recipients: picked.size, emails: byEmail.length, phones: 0, audience,
      });
      // قیف (WHOSUP-291): دعوت به آدم‌ها و، جدا، به گروه‌هایی که کامل انتخاب شدند.
      const fullGroups = audience === 'picked'
        ? [...groupMembers.values()].filter((ids) => ids.length > 0 && ids.every((u) => picked.has(u))).length
        : 0;
      const people = picked.size + byEmail.length;
      if (people) trackFunnel('invite_sent', { id: created.id, target: 'friend', count: people });
      if (fullGroups) trackFunnel('invite_sent', { id: created.id, target: 'group', count: fullGroups });
      qc.invalidateQueries({ queryKey: ['mine'] });
      /*
       * هر کسی که دعوت شد حالا توی فهرستِ دوستانه (مایگریشن 0075) — چه با
       * حساب (درخواستِ دوستی) چه بی‌حساب (ردیفِ دفترچه). بدونِ این، صفحه‌ی
       * دوستان همون فهرستِ قبلی رو نشون می‌داد و انگار هیچ اتفاقی نیفتاده.
       */
      qc.invalidateQueries({ queryKey: ['friends'] });
      qc.invalidateQueries({ queryKey: ['contactFriends'] });
      // هر دعوتی که رفت، یکی از سهمیه‌ی «تا تاییدِ دوستی» رو خرج کرده.
      qc.invalidateQueries({ queryKey: ['inviteAllowance'] });
      // اولین سیگنال = لحظه‌ی قرارِ WHOSUP-71: کارتِ «اعلان روشن کن» باید
      // همین حالا روی صفحه‌ی سیگنالِ تازه دربیاید، نه با جوابِ کهنه‌ی کش.
      qc.invalidateQueries({ queryKey: ['hasFirstActivity'] });
      /*
       * «یک لینک هم بساز»: موبایل این‌جا شیتِ اشتراک رو باز می‌کنه؛ روی وب
       * شیتی نیست و «اشتراک» یعنی کپیِ لینک (همون قاعده‌ی SignalPage). کپیِ
       * موفق با state به صفحه‌ی سیگنال اعلام می‌شه تا همون‌جا «کپی شد» بگه.
       * اگه کلیپ‌بورد اجازه نده (مثلاً فاصله‌ی ساخت از کلیک طولانی شده)،
       * بی‌صدا رد می‌شیم — دکمه‌ی اشتراک توی خودِ صفحه‌ی سیگنال هست.
       */
      let linkCopied = false;
      if (wantLink && created.shareToken && created.status !== 'held') {
        try {
          await navigator.clipboard.writeText(signalUrl(created.shareToken));
          linkCopied = true;
          trackFunnel('share', { id: created.id, source: 'signal', method: 'copy', link: 'public' });
        } catch { /* کلیپ‌بورد اجازه نداد */ }
      }
      // `created`: «برگشت» در صفحه‌ی سیگنال به «برنامه‌های من» می‌رود، نه به صفحه‌ی
      // قبل از فرم — که می‌تواند برنامه‌ی قبلی باشد (WHOSUP-342، SETUP-04 قدمِ ۲).
      nav(`/signal/${created.id}`, {
        replace: true,
        state: { created: true, ...(linkCopied ? { linkCopied: true } : {}) },
      });
    },
    onError: (e) => {
      if (handleFilterBlocked(e)) return;
      reportError('web.signal.create', e);
      /*
       * سقفِ دعوتِ دوستیِ تاییدنشده (مایگریشن 0141). فرم از قبل ردیفِ پرشده
       * رو خاکستری می‌کنه، پس این‌جا فقط حالتِ مسابقه‌ایه — سهمیه‌ای که از
       * یه دستگاهِ دیگه بینِ باز شدنِ فرم و زدنِ «بفرست» خرج شده. «خطا در
       * ارسال» برای این حالت دروغه: کاربر باید دلیل رو ببینه.
       */
      setError(isInviteCapError(e)
        ? t('inviteCapError', { n: UNACCEPTED_INVITE_CAP })
        : t('errorSending'));
      // سهمیه‌ها کهنه‌ان: همین حالا دوباره بپرس تا ردیفِ درست خاکستری شه.
      qc.invalidateQueries({ queryKey: ['inviteAllowance'] });
    },
  });

  const submit = () => {
    setError(null);
    if (!title.trim()) { setError(t('writeFirst')); return; }
    // «بهرحال می‌رم» از لحظه‌ی ساخت قطعیه (مایگریشن 0127) و برنامه‌ی قطعیِ
    // بی‌زمان معنا ندارد — دیتابیس هم همین را رد می‌کند.
    if (kind === 'definite' && !(whenMode === 'exact' && date)) {
      setError(t('definiteNeedsTime'));
      return;
    }
    // نصاب یعنی قطعیِ خودکار، و قطعی زمان می‌خواهد — دیتابیس هم رد می‌کند.
    if (kind !== 'definite' && Number(quorum) > 0 && !(whenMode === 'exact' && date)) {
      setError(t('quorumNeedsTime'));
      return;
    }
    // سیگنالِ بدون گیرنده معنی نداره: هیچ‌کس نمی‌بیندش. به‌جای اینکه بی‌صدا
    // بره، جلوش گرفته می‌شه — و «همه‌ی دوستانم» یه گزینه‌ی واقعیه.
    // مگر با «یک لینک هم بساز»: لینک خودش راهِ رسیدنه (WHOSUP-117) — سیگنال
    // ساخته می‌شه، لینک کپی می‌شه، و از راهِ همون لینک جواب می‌گیره.
    if (!everyone && picked.size === 0 && byEmail.length === 0 && !wantLink) {
      setError(t('noOnePickedBody'));
      return;
    }
    send.mutate(everyone ? 'friends' : 'picked');
  };

  if (!profile) {
    return <div className="feed"><Card><Note tone="brand">{t('oneStepLeft')}</Note></Card></div>;
  }

  const previewWhen = date
    ? signalWhenLabel({ startsAt: exactStart()?.toISOString(), dateOnly: !time })
    : '';

  /**
   * انتخابی که با هیچ‌کدام از تاریخ‌های رخدادِ مبدأ نمی‌خواند — هشدار، نه سد:
   * شاید قصدش «شبِ بعد از کنسرت» باشد. فقط وقتی اجراها رسیده‌اند، وگرنه وسطِ
   * لود شدن هشدارِ دروغ می‌داد.
   *
   * گزینه‌های تقریبی هم سنجیده می‌شوند نه فقط تاریخِ دقیق: «این آخر هفته»
   * برای رخدادِ سه‌شنبه همان‌قدر ناهم‌خوان است که ۲۲ آگوستِ دقیق. متنِ آزاد
   * سنجیدنی نیست (approxDays برایش null می‌دهد).
   */
  const chosenRange = whenMode === 'exact' && date
    ? (() => { const d = dayFromIso(date); return d ? { start: d, end: null } : null; })()
    : whenMode === 'approx' ? approxDays(preset) : null;
  const offEventDate = !!chosenRange && !!fromEvent.data && fromOccurrences.isSuccess
    && !choiceOnEvent(fromEvent.data, fromOccurrences.data ?? [], chosenRange.start, chosenRange.end);

  // پیش‌پرِ رخداد فقط تا وقتی «ویرایش» زده نشده خلاصه می‌شه؛ عنوانِ خالی
  // هم فرم رو باز نگه می‌داره.
  const eventSummary = !!fromEventId && !eventEditOpen && !!title.trim();
  const whenSummary = whenMode === 'exact' ? previewWhen
    : whenMode === 'approx'
      ? t(APPROX_PRESETS.find((p) => p.token === preset)?.key ?? APPROX_PRESETS[0].key)
      : whenMode === 'coordinate' ? t('coordinate') : freeWhen.trim();
  const moreShown = moreOpen || chain !== 0 || !!deadlineDate;
  const tagNames = (tags.data ?? []).filter((x) => tagIds.includes(x.id))
    .map(tagLabel).join(' · ');

  return (
    <div className="feed">
      <h1 className="feed-title">{t('postIdea')}</h1>

      {eventSummary ? (
        <Card>
          <h3>
            {t('fromEventSummary')}
            {' · '}
            <button type="button" className="linklike"
              onClick={() => setEventEditOpen(true)}>{t('edit')}</button>
          </h3>
          <p><strong>{title}</strong></p>
          {tagNames ? <p className="dim">{tagNames}</p> : null}
          {whenSummary ? <p className="dim">🕒 {whenSummary}</p> : null}
          {place.trim() ? <p className="dim">📍 {place}</p> : null}
          {offEventDate ? <Note tone="warn">{t('signalDateOffEvent')}</Note> : null}
        </Card>
      ) : (
        <Card>
          <h3>{t('plan')}</h3>
          <input className="input" placeholder={t('titlePlaceholder')} value={title}
            onChange={(e) => setTitle(e.target.value)} />
        </Card>
      )}

      <Card>
        <h3>{t('kind')}</h3>
        {/* عمداً دراپ‌دان نیست: گزینه‌ها با توضیحشون از اول بازن و بعد از
            انتخاب به همون یه کارت جمع می‌شن (خواسته‌ی ۲۰۲۶-۰۸-۲۱).
            سیگنالی که از روی یک رخداد ساخته می‌شه «خودم میزبانم» نداره —
            میزبانِ اون رخداد یکی دیگه‌ست (core/domain/signalKinds.ts). */}
        <PickList value={kind}
          items={kindsFor({ fromEvent: !!fromEventId }).map((k) => ({
            value: k.kind, label: `${k.emoji} ${t(k.titleKey)}`, hint: t(k.subKey),
          }))}
          onChange={setKind} />
      </Card>

      {/* ساخت از رخداد: تگ‌ها هم مثلِ کِی/کجا در خلاصه‌اند، تا «ویرایش». */}
      {!eventSummary && (tags.data ?? []).length ? (
        <Card>
          <h3>{t('category')}</h3>
          <TagPicker value={tagIds} onChange={setTagIds} tags={tags.data ?? []} />
        </Card>
      ) : null}

      {!eventSummary ? (<>
      <Card>
        <h3>{t('when')}</h3>
        <Select value={whenMode}
          items={[
            { value: 'approx', label: t(APPROX_PRESETS[0].key) },
            { value: 'coordinate', label: t('coordinate') },
            { value: 'exact', label: t('pickDate') },
            { value: 'free', label: t('whenFreeText') },
          ]}
          onChange={(m) => { setWhenMode(m); if (m === 'approx') setPreset(APPROX_PRESETS[0].token); }} />

        {/* متنِ آزاد عمداً همون ستونِ approx رو پر می‌کنه و نشانه نمی‌گیره:
            «شنبه صبح زود» ترجمه‌پذیر نیست و باید همون‌طور که نوشته شده بمونه. */}
        {whenMode === 'free' ? (
          <input className="input" placeholder={t('whenFreeTextPlaceholder')} value={freeWhen}
            onChange={(e) => setFreeWhen(e.target.value)} />
        ) : null}

        {whenMode === 'exact' ? (
          <>
            <div className="two-up">
              <label className="field">
                <span>{t('pickDate')}</span>
                <input className="input" type="date" value={date}
                  onChange={(e) => setDate(e.target.value)} />
              </label>
              <label className="field">
                <span>{t('whenFreeText')}</span>
                <input className="input" type="time" value={time}
                  onChange={(e) => setTime(e.target.value)} />
              </label>
            </div>
            {date ? (
              <p className="dim">
                {previewWhen}
                {' · '}
                <button type="button" className="linklike"
                  onClick={() => { setDate(''); setTime(''); }}>{t('clear')}</button>
              </p>
            ) : <p className="dim">{t('pickDayHint')}</p>}
          </>
        ) : null}
        {/* زیرِ کلِ «کِی؟» و نه فقط تقویم: انتخابِ تقریبی هم می‌تواند ناهم‌خوان باشد. */}
        {offEventDate ? <Note tone="warn">{t('signalDateOffEvent')}</Note> : null}
      </Card>

      {/* «کجا» کادرِ خودش. */}
      <Card>
        <h3>{t('where')}</h3>
        <input className="input" placeholder={t('placePlaceholder')} value={place}
          onChange={(e) => setPlace(e.target.value)} />
      </Card>
      </>) : null}

      {/* یک کادر برای همه‌ی «اطلاعاتِ بیشتر» — تا دیروز دو تا بود (شرح و
          جزئیات) و کاربر نمی‌دونست کدوم مالِ چیه. هزینه هم یکی از همون
          پیشنهادهای پشتِ «؟» است، نه فیلدِ جدا. */}
      <Card>
        <h3>{t('suggestDescription')}<Help lines={SIGNAL_NOTE_KEYS.map((k) => t(k))} /></h3>
        <textarea className="input textarea" rows={4} value={note}
          placeholder={t('suggestDescriptionPh')}
          onChange={(e) => setNote(e.target.value)} />
      </Card>

      <Card>
        {/* ظرفیت فقط برای میزبانی معنا دارد — خانه‌ی ۶نفره (مایگریشن 0131). */}
        {kind === 'hosting' ? (
          <label className="field">
            <span>{t('capacityLabel')}</span>
            <input className="input" type="number" min={1} value={capacity}
              onChange={(e) => setCapacity(e.target.value)} />
          </label>
        ) : null}
        {/* نصاب برای definite بی‌معنی است — آن از اول قطعی است. */}
        {kind !== 'definite' ? (
          <label className="field">
            <span>{t('quorumLabel')}</span>
            <input className="input" type="number" min={2} value={quorum}
              onChange={(e) => setQuorum(e.target.value)} />
          </label>
        ) : null}
      </Card>

      {/* ــ دعوت، جمع‌وجور ــ
          تا دیروز این کارت نصفِ فرم بود: یه کارتِ بزرگِ «همه»، یه ردیفِ
          گروه‌ها، یه دیوارِ چیپِ دوستان، و فرمِ ایمیلِ همیشه‌باز. حالا سه
          سطر: کلیدِ «همه»، یه انتخابگرِ جستجودار (همون رفتارِ انتخابِ تگ)، و
          دعوتِ بیرون از اپ که تا خواسته نشه باز نمی‌شه. */}
      <Card>
        {/* توضیح‌ها پشتِ «؟» — همیشه‌دیده‌شدن جای فرم را می‌گرفت (WHOSUP-282). */}
        <h3>{t('whoToTell')}<Help lines={[t('fomoNote'), t('audienceAllHint')]} /></h3>

        <Checkbox label={t('audienceAll')} checked={everyone}
          onChange={(v) => { setEveryone(v); setPicked(new Set()); }} />

        {/* با «همه»، انتخابِ تک‌تک معنی نداره و فقط گمراه‌کننده‌ست. */}
        {!everyone ? (
          <>
            {friends.isLoading ? <p className="dim">…</p> : null}
            {!friends.isLoading && peopleItems.length === 0
              ? <p className="dim">{t('noFriendsYetCreate')}</p>
              : (
                <MultiSelect logKey="invite" placeholder={t('pickPeople')}
                  items={peopleItems} value={pickedKeys} chipKeys={chipKeys}
                  onToggle={(key) => {
                    if (key.startsWith('g-')) {
                      toggleGroup(groupMembers.get(key) ?? []);
                      return;
                    }
                    // `c-` (فقط-شماره‌تلفن) به این‌جا نمی‌رسه — دکمه‌ش غیرفعاله.
                    if (key.startsWith('u-')) toggle(key.slice(2));
                  }} />
              )}
          </>
        ) : null}

        {/* ــ دعوت با ایمیل ــ
            جایگزینِ وبیِ «دعوت از دفترچه‌ی گوشی»ِ موبایل: مرورگر به مخاطبین
            دسترسی نداره، پس آدرس دستی تایپ می‌شه. نتیجه‌ش یکیه — یه ایمیل با
            لینکِ شخصی می‌ره و طرف بدون حساب جواب می‌ده.

            جمع‌شده، چون کارِ اکثرِ سیگنال‌ها نیست: کسی که ایمیل می‌خواد
            خودش بازش می‌کنه. */}
        {byEmail.length ? (
          <div className="chiprow chips-wrap">
            {byEmail.map((i) => (
              <button key={i.email} type="button" className="chip on"
                title={i.email}
                onClick={() => setByEmail(removeInvitee(byEmail, i.email))}>
                {inviteeLabel(i)} ×
              </button>
            ))}
          </div>
        ) : null}

        <button type="button" className="linklike" aria-expanded={outsideOpen}
          onClick={() => setOutsideOpen((x) => !x)}>
          {outsideOpen ? `${t('inviteOutside')} ▴` : `+ ${t('inviteOutside')}`}
        </button>

        {outsideOpen ? (
          <>
            <p className="dim">{t('inviteByEmailSub')}</p>
            <div className="two-up">
              <input className="input" type="email" inputMode="email"
                placeholder={t('inviteEmailPlaceholder')}
                value={emailDraft}
                onChange={(e) => { setEmailDraft(e.target.value); setEmailError(null); }}
                // Enter توی فرمِ ایمیل یعنی «اضافه کن»، نه «سیگنال رو بفرست».
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addByEmail(); } }} />
              <input className="input"
                placeholder={t('inviteNamePlaceholder')}
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addByEmail(); } }} />
            </div>
            {emailError ? <p className="error">{emailError}</p> : null}
            <button type="button" className="btn" disabled={!emailDraft.trim()}
              onClick={addByEmail}>
              {t('inviteEmailAdd')}
            </button>

            {/* لینکِ عمومی: برای کسی که نه دوسته نه ایمیلش رو داری — همون
                کارتِ «یک لینک هم بساز»ِ موبایل، همین‌جا توی همین بخش. بعد از
                ساخت، لینک کپی می‌شه (وب شیتِ اشتراکِ سیستمی نداره). */}
            <Checkbox label={t('alsoMakeLink')} checked={wantLink} onChange={setWantLink} />
            <p className="dim">{t('alsoMakeLinkHint')}</p>
          </>
        ) : null}
      </Card>

      {/* زنجیره و مهلت کم‌استفاده‌ان — پشتِ «گزینه‌های بیشتر»، تهِ فرم (WHOSUP-282). */}
      <Card>
        <button type="button" className="linklike" aria-expanded={moreShown}
          onClick={() => setMoreOpen((x) => !x)}>
          {moreShown ? `${t('moreSignalOptions')} ▴` : `+ ${t('moreSignalOptions')}`}
        </button>
        {moreShown ? (
          <>
            {/* توضیحِ زنجیره پشتِ «؟» — گزینه‌های «خاموش / +۱ / +۲» تقریباً خودگویان. */}
            <h3>{t('chainInvite')}<Help text={t('chainNote')} /></h3>
            {/* مقدارِ Select رشته‌ست؛ عدد بودنِ chain همین‌جا ترجمه می‌شه. */}
            <Select value={String(chain)}
              items={CHAIN.map((c) => ({ value: String(c.value), label: c.label }))}
              onChange={(v) => setChain(Number(v))} />
            <label className="field">
              <span>{t('deadlineLabel')}</span>
              <div className="two-up">
                <input className="input" type="date" value={deadlineDate}
                  onChange={(e) => setDeadlineDate(e.target.value)} />
                <input className="input" type="time" value={deadlineTime}
                  onChange={(e) => setDeadlineTime(e.target.value)} />
              </div>
            </label>
          </>
        ) : null}
      </Card>

      {error ? <p className="error">{error}</p> : null}

      <button type="button" className="btn fill block"
        disabled={send.isPending} onClick={submit}>
        {/* `send` عمداً استفاده نشد: همون کلید روی بازخورد، چت و منویِ جواب هم
            هست، و این دکمه کارِ متفاوتی می‌کنه — دعوت‌ها رو می‌فرسته. */}
        {send.isPending ? '…' : t('sendInvites')}
      </button>
    </div>
  );
}
